import React, { useState, useEffect } from "react";
import { Card, Group, Text, Badge, Button, Divider, ScrollArea, Title } from "@mantine/core";
import { BellIcon, ArchiveBoxIcon } from "@heroicons/react/24/outline";
import { useTheme } from "../../ThemeContext";
import { getUserNotifications, markNotificationAsRead, markAllNotificationsAsRead } from "../../api/api";

const NotificationComponent = ({ userName }) => {
  const { theme } = useTheme();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      if (!user) return;

      const response = await getUserNotifications({
        limit: 20,
        unreadOnly: false
      });
      
      if (response.data.success) {
        setNotifications(response.data.data || []);
        setUnreadCount(response.data.unreadCount || 0);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // Refresh notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleArchive = async (id) => {
    try {
      await markNotificationAsRead(id);
      setNotifications(notifications.filter((notification) => notification._id !== id));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      setUnreadCount(0);
      setNotifications(notifications.map(n => ({ ...n, isRead: true })));
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const darkMode = theme === "dark";

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Card 
      shadow="md"
      padding="lg"
      radius="md"
      style={{
        maxWidth: 500,
        margin: "0 auto",
        zIndex: 1000,
        backgroundColor: darkMode ? "#1c1f26" : "#f8f9fa",
        color: darkMode ? "#e9ecef" : "#343a40",
      }}
    >
      <Group position="apart">
        <Title order={4} style={{ color: darkMode ? "#e9ecef" : "#343a40" }}>
         {userName} Notifications
        </Title>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <Badge
              color="red"
              size="lg"
              variant="filled"
            >
              {unreadCount}
            </Badge>
          )}
          {unreadCount > 0 && (
            <Button 
              variant="subtle" 
              size="xs" 
              onClick={handleMarkAllAsRead}
              style={{ color: darkMode ? "#adb5bd" : "#495057" }}
            >
              Mark all read
            </Button>
          )}
        </div>
      </Group>

      <Divider my="sm" style={{ borderColor: darkMode ? "#495057" : "#dee2e6" }} />

      <ScrollArea style={{ maxHeight: 300 }}>
        {loading ? (
          <Text size="sm" align="center" style={{ color: darkMode ? "#868e96" : "#adb5bd", padding: "20px" }}>
            Loading notifications...
          </Text>
        ) : notifications.length === 0 ? (
          <Text size="sm" align="center" style={{ color: darkMode ? "#868e96" : "#adb5bd", marginTop: "12px" }}>
            No new notifications.
          </Text>
        ) : (
          notifications.map((notification) => (
            <Card
              key={notification._id}
              shadow="sm"
              radius="md"
              mb="sm"
              style={{
                backgroundColor: !notification.isRead 
                  ? darkMode 
                    ? "#2e3138" 
                    : "#e3f2fd"
                  : darkMode
                  ? "#1c1f26"
                  : "#f8f9fa",
                padding: "12px",
                transition: "transform 0.2s ease",
                borderLeft: !notification.isRead ? "3px solid #2196f3" : "none",
              }}
              className="hover:shadow-md"
            >
              <Group position="apart">
                <div style={{ flex: 1 }}>
                  <Text
                    weight={!notification.isRead ? 600 : 400}
                    size="sm"
                    style={{
                      color: darkMode ? "#adb5bd" : "#495057",
                      lineHeight: "1.4",
                    }}
                  >
                    {notification.title}
                  </Text>
                  <Text
                    size="xs"
                    style={{
                      color: darkMode ? "#868e96" : "#6c757d",
                      marginTop: "4px",
                    }}
                  >
                    {notification.description}
                  </Text>
                </div>
                <Button
                  variant="subtle"
                  color={darkMode ? "gray" : "red"}
                  compact
                  size="xs"
                  leftIcon={<ArchiveBoxIcon size={14} />}
                  onClick={() => handleArchive(notification._id)}
                  style={{
                    color: darkMode ? "#adb5bd" : "#868e96",
                    fontSize: "0.75rem",
                  }}
                >
                  Archive
                </Button>
              </Group>
              <Text size="xs" style={{ color: darkMode ? "#868e96" : "#adb5bd", marginTop: "4px" }}>
                {formatDate(notification.createdAt)}
              </Text>
            </Card>
          ))
        )}
      </ScrollArea>
    </Card>
  );
};

export default NotificationComponent;
